import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useRef, useState } from 'react';

import { ApiError, api } from '../../../lib/api-client';

import type { ConversationListItem } from '../../../lib/types';
import type {
  AvailablePlaybook,
  CoAttendantListItem,
  CoAttendantRole,
  CoachingMessage,
  ConversationComposerStateWire,
  ConversationTakeoverResult,
  DiscrepancyIndicatorWire,
  PlaybookChangeResult,
  QualityIndicators,
} from '../types';

export const COATTENDANCE_KEY = ['coattendance'] as const;

/**
 * Hooks da Tela 2 Acompanhamento Silencioso (peer-coordination).
 *
 * - `useActiveCoAttendance` — sessões ativas do PRÓPRIO supervisor (D1: não há board do time).
 * - `useCoAttendanceSession` — ciclo de sessão (D2): reusa a ativa da conversa,
 *   senão `start` shadow_observer; trata 409 re-buscando active; `end()` no unmount/Esc.
 * - `useComposerState` — espelho do "atendente digitando" (poll 5s + invalidação por WS).
 * - `useQualityIndicators` — KPIs vivos do espelho (poll 15s — D13, sem WS nesta onda).
 */

export function useActiveCoAttendance(): {
  data: CoAttendantListItem[] | undefined;
  isLoading: boolean;
  isError: boolean;
} {
  const q = useQuery({
    queryKey: [...COATTENDANCE_KEY, 'active'],
    queryFn: async () => (await api.coattendanceActive()).data,
    staleTime: 10 * 1000,
  });
  return { data: q.data, isLoading: q.isLoading, isError: q.isError };
}

function findActiveFor(
  conversationId: string,
  items: CoAttendantListItem[] | undefined,
): CoAttendantListItem | undefined {
  return (items ?? []).find((i) => i.conversationId === conversationId && i.removedAt === null);
}

export interface CoAttendanceSession {
  coAttendanceId: string | null;
  role: CoAttendantListItem['role'] | null;
  isResolving: boolean;
  error: unknown;
  end: () => void;
}

/**
 * Resolve (uma vez) o `coAttendanceId` da conversa: reusa a sessão ativa do
 * supervisor OU inicia `shadow_observer`. Em 409 (corrida — outra aba/sessão
 * acabou de criar) re-busca `active` e reusa. Encerra no unmount/Esc.
 */
export function useCoAttendanceSession(conversationId: string | undefined): CoAttendanceSession {
  const qc = useQueryClient();
  const [coAttendanceId, setCoAttendanceId] = useState<string | null>(null);
  const [role, setRole] = useState<CoAttendantListItem['role'] | null>(null);
  const [isResolving, setIsResolving] = useState(false);
  const [error, setError] = useState<unknown>(null);
  // Ref para o cleanup do unmount sempre ver o id mais recente sem re-disparar o effect.
  const idRef = useRef<string | null>(null);
  idRef.current = coAttendanceId;

  useEffect(() => {
    if (conversationId === undefined) return;
    let cancelled = false;
    setIsResolving(true);
    setError(null);

    const reuseOrThrow = async (): Promise<void> => {
      const refreshed = (await api.coattendanceActive()).data;
      const match = findActiveFor(conversationId, refreshed);
      if (match === undefined) {
        throw new Error('coattendance conflict without an active session to reuse');
      }
      if (cancelled) return;
      setCoAttendanceId(match.id);
      setRole(match.role);
    };

    void (async () => {
      try {
        const active = (await api.coattendanceActive()).data;
        const existing = findActiveFor(conversationId, active);
        if (existing !== undefined) {
          if (cancelled) return;
          setCoAttendanceId(existing.id);
          setRole(existing.role);
          return;
        }
        try {
          const started = await api.coattendanceStart({ conversationId, role: 'shadow_observer' });
          if (cancelled) return;
          setCoAttendanceId(started.coAttendanceId);
          setRole('shadow_observer');
        } catch (startErr) {
          // 409 — sessão criada concorrentemente; reusa via active.
          if (startErr instanceof ApiError && startErr.status === 409) {
            await reuseOrThrow();
          } else {
            throw startErr;
          }
        }
      } catch (err) {
        if (!cancelled) setError(err);
      } finally {
        if (!cancelled) setIsResolving(false);
      }
    })();

    return () => {
      cancelled = true;
      const id = idRef.current;
      if (id !== null) {
        // Não vaza sessão — encerra ao desmontar/sair. invalida active para refletir.
        void api
          .coattendanceEnd(id)
          .then(() => qc.invalidateQueries({ queryKey: [...COATTENDANCE_KEY, 'active'] }))
          .catch(() => {
            /* end best-effort — sessão pode já ter sido encerrada no servidor */
          });
      }
    };
  }, [conversationId, qc]);

  const end = (): void => {
    const id = idRef.current;
    if (id === null) return;
    // Limpa o ref ANTES do async para o cleanup do unmount (que lê idRef) não re-encerrar
    // a mesma sessão (Esc → end() → navigate → unmount → cleanup veria o id stale).
    idRef.current = null;
    void api
      .coattendanceEnd(id)
      .then(() => qc.invalidateQueries({ queryKey: [...COATTENDANCE_KEY, 'active'] }))
      .catch(() => {
        /* best-effort */
      });
    setCoAttendanceId(null);
    setRole(null);
  };

  return { coAttendanceId, role, isResolving, error, end };
}

export function useComposerState(conversationId: string | undefined): {
  data: ConversationComposerStateWire | undefined;
  isLoading: boolean;
  isError: boolean;
} {
  const q = useQuery({
    queryKey: ['composer-state', conversationId],
    enabled: conversationId !== undefined,
    queryFn: () => {
      if (conversationId === undefined) throw new Error('conversation id required');
      return api.getComposerState(conversationId);
    },
    // Poll 5s + invalidação por WS `conversation.attendant-composing` (use-acompanhamento-ws-sync).
    refetchInterval: 5 * 1000,
    staleTime: 2 * 1000,
  });
  return { data: q.data, isLoading: q.isLoading, isError: q.isError };
}

export function useQualityIndicators(conversationId: string | undefined): {
  data: QualityIndicators | undefined;
  isLoading: boolean;
  isError: boolean;
} {
  const q = useQuery({
    queryKey: ['quality-indicators', conversationId],
    enabled: conversationId !== undefined,
    queryFn: () => {
      if (conversationId === undefined) throw new Error('conversation id required');
      return api.getQualityIndicators(conversationId);
    },
    // WS `quality-indicators.updated` não consumido nesta onda (D13) → poll 15s.
    refetchInterval: 15 * 1000,
    staleTime: 10 * 1000,
  });
  return { data: q.data, isLoading: q.isLoading, isError: q.isError };
}

// ─── T2-2 — Ferramentas do espelho ─────────────────────────────────────────

export const COACHING_KEY = 'coaching-messages';
export const SESSION_NOTE_KEY = 'session-note';
export const DISCREPANCY_KEY = 'discrepancy-indicator';

export interface UseCoachingMessages {
  messages: CoachingMessage[];
  isLoading: boolean;
  isError: boolean;
  /** Envio com guard anti duplo-submit. Ignora texto vazio. */
  send: (text: string, visibleToAttendant: boolean) => Promise<void>;
  isSending: boolean;
  /** Erro do último envio (null quando ok) — a UI mostra um `role=alert`. */
  sendError: unknown;
}

/**
 * D5 — canal de coaching DIRIGIDO (supervisor↔atendente). `GET after&limit`
 * (cursor por timestamp) + `send` com guard anti duplo-submit. Lista ordenada
 * por timestamp asc. Invalidação por WS `coaching-message.created`
 * (use-acompanhamento-ws-sync).
 */
export function useCoachingMessages(coAttendanceId: string | null): UseCoachingMessages {
  const qc = useQueryClient();
  const [isSending, setIsSending] = useState(false);
  const [sendError, setSendError] = useState<unknown>(null);

  const q = useQuery({
    queryKey: [COACHING_KEY, coAttendanceId],
    enabled: coAttendanceId !== null,
    queryFn: async () => {
      if (coAttendanceId === null) throw new Error('coAttendance id required');
      return (await api.listCoachingMessages(coAttendanceId, { limit: 200 })).data;
    },
    staleTime: 5 * 1000,
  });

  const messages = [...(q.data ?? [])].sort((a, b) => a.timestamp.localeCompare(b.timestamp));

  const send = useCallback(
    async (text: string, visibleToAttendant: boolean): Promise<void> => {
      const trimmed = text.trim();
      // Guard anti duplo-submit: vazio ou já enviando → ignora.
      if (trimmed.length === 0 || coAttendanceId === null || isSending) return;
      setIsSending(true);
      setSendError(null);
      try {
        await api.postCoachingMessage(coAttendanceId, { text: trimmed, visibleToAttendant });
        await qc.invalidateQueries({ queryKey: [COACHING_KEY, coAttendanceId] });
      } catch (err) {
        // Async não engole falha — expõe para a UI mostrar um alerta.
        setSendError(err);
      } finally {
        setIsSending(false);
      }
    },
    [coAttendanceId, isSending, qc],
  );

  return { messages, isLoading: q.isLoading, isError: q.isError, send, isSending, sendError };
}

export type SessionNoteSaveState = 'idle' | 'saving' | 'saved' | 'error';

export interface UseSessionNote {
  text: string;
  setText: (value: string) => void;
  saveState: SessionNoteSaveState;
  version: number | null;
}

const SESSION_NOTE_DEBOUNCE_MS = 3000;

/**
 * D6 — nota privada da sessão (single scratchpad, NÃO o array do mockup). `GET`
 * no load (404 → texto vazio, sem erro); auto-save debounced ~3s via `POST`
 * (upsert, incrementa `version`). SEM If-Match. async não engole falha
 * (`saveState='error'`).
 */
export function useSessionNote(coAttendanceId: string | null): UseSessionNote {
  const [text, setTextState] = useState('');
  const [version, setVersion] = useState<number | null>(null);
  const [saveState, setSaveState] = useState<SessionNoteSaveState>('idle');
  const loadedRef = useRef(false);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Load inicial (404 → vazio).
  useEffect(() => {
    if (coAttendanceId === null) return;
    let cancelled = false;
    loadedRef.current = false;
    void api
      .getSessionNote(coAttendanceId)
      .then((note) => {
        if (cancelled) return;
        if (note !== null) {
          setTextState(note.text);
          setVersion(note.version);
        }
      })
      .catch(() => {
        /* erro de load não-404 é silencioso aqui — a nota começa vazia */
      })
      .finally(() => {
        if (!cancelled) loadedRef.current = true;
      });
    return () => {
      cancelled = true;
    };
  }, [coAttendanceId]);

  const setText = useCallback(
    (value: string): void => {
      setTextState(value);
      if (coAttendanceId === null) return;
      setSaveState('saving');
      if (timerRef.current !== null) clearTimeout(timerRef.current);
      timerRef.current = setTimeout(() => {
        void api
          .upsertSessionNote(coAttendanceId, { text: value })
          .then((note) => {
            setVersion(note.version);
            setSaveState('saved');
          })
          .catch(() => {
            // async não engole falha — sinaliza erro visível.
            setSaveState('error');
          });
      }, SESSION_NOTE_DEBOUNCE_MS);
    },
    [coAttendanceId],
  );

  // Limpa o timer pendente no unmount.
  useEffect(
    () => () => {
      if (timerRef.current !== null) clearTimeout(timerRef.current);
    },
    [],
  );

  return { text, setText, saveState, version };
}

export type DiscrepancyIndicatorReal = Exclude<
  DiscrepancyIndicatorWire,
  { discrepancyApplicable: false }
>;

export interface UseDiscrepancy {
  /** null quando não aplicável (regra do silêncio) ou ainda carregando. */
  indicator: DiscrepancyIndicatorReal | null;
  isLoading: boolean;
  isError: boolean;
}

/**
 * D7 — discrepância de visibilidade (só bond adversarial). Narrow do
 * `discrepancyApplicable:false` → null. Invalidação por WS
 * `coattendance.discrepancy-detected` (use-acompanhamento-ws-sync).
 */
export function useDiscrepancy(conversationId: string | undefined): UseDiscrepancy {
  const q = useQuery({
    queryKey: [DISCREPANCY_KEY, conversationId],
    enabled: conversationId !== undefined,
    queryFn: () => {
      if (conversationId === undefined) throw new Error('conversation id required');
      return api.getDiscrepancyIndicator(conversationId);
    },
    staleTime: 15 * 1000,
  });

  const wire = q.data;
  const indicator: DiscrepancyIndicatorReal | null =
    wire !== undefined && !('discrepancyApplicable' in wire) ? wire : null;

  return { indicator, isLoading: q.isLoading, isError: q.isError };
}

/**
 * D10 — conversas paralelas REAIS do atendente (`listConversations` in-progress)
 * menos a conversa atual. Degrada honesto para [] quando vazio/sem atendente.
 */
export function useParallelConversations(
  attendantUserId: string | null,
  excludeConversationId: string,
): { data: ConversationListItem[]; isLoading: boolean; isError: boolean } {
  const q = useQuery({
    queryKey: ['parallel-conversations', attendantUserId],
    enabled: attendantUserId !== null,
    queryFn: async () => {
      if (attendantUserId === null) throw new Error('attendant id required');
      const res = await api.listConversations({
        assignedToUserId: attendantUserId,
        status: 'in-progress',
      });
      return res.data;
    },
    staleTime: 10 * 1000,
  });

  const data = (q.data ?? []).filter((c) => c.id !== excludeConversationId);
  return { data, isLoading: q.isLoading, isError: q.isError };
}

// ─── T2-3 — Modos + ações de força ──────────────────────────────────────────

export interface UseChangeRole {
  /**
   * Troca o role da sessão mid-session (D3). `supervisor_monitor` exige
   * `invisibleJustification` (backend bloqueia sem ela). Guard anti duplo-submit
   * + invalida `active`. async não engole falha (`error`).
   */
  /** Resolve `true` só quando o servidor confirma a troca; `false` em erro/guard (a UI não deve refletir o novo modo em `false`). */
  changeRole: (role: CoAttendantRole, invisibleJustification?: string) => Promise<boolean>;
  isChanging: boolean;
  error: unknown;
}

/**
 * D3 — troca de modo (role) da sessão de co-atendimento. `PATCH /coattendance/:id/role`.
 * Quando muda para `supervisor_monitor` envia a justificativa obrigatória (1-500),
 * espelhando a exigência do backend. Guard anti duplo-submit.
 */
export function useChangeRole(coAttendanceId: string | null): UseChangeRole {
  const qc = useQueryClient();
  const [isChanging, setIsChanging] = useState(false);
  const [error, setError] = useState<unknown>(null);

  const changeRole = useCallback(
    async (role: CoAttendantRole, invisibleJustification?: string): Promise<boolean> => {
      // Guard anti duplo-submit: sem sessão ou troca em andamento → ignora.
      if (coAttendanceId === null || isChanging) return false;
      setIsChanging(true);
      setError(null);
      try {
        const justification = invisibleJustification?.trim();
        await api.coattendanceRole(coAttendanceId, {
          role,
          ...(justification !== undefined && justification.length > 0
            ? { invisibleJustification: justification }
            : {}),
        });
        await qc.invalidateQueries({ queryKey: [...COATTENDANCE_KEY, 'active'] });
        return true;
      } catch (err) {
        // async não engole falha — UI mostra alerta e NÃO reflete o novo modo.
        setError(err);
        return false;
      } finally {
        setIsChanging(false);
      }
    },
    [coAttendanceId, isChanging, qc],
  );

  return { changeRole, isChanging, error };
}

/**
 * D8 — playbooks reais disponíveis p/ o force-playbook-switch
 * (`GET /supervisor-actions/conversations/:id/available-playbooks`, array cru).
 * Degrada honesto: erro → `data: []` (o modal mostra "nenhum playbook disponível").
 */
export function useAvailablePlaybooks(
  conversationId: string,
  enabled: boolean,
): { data: AvailablePlaybook[]; isLoading: boolean; isError: boolean } {
  const q = useQuery({
    queryKey: ['available-playbooks', conversationId],
    enabled,
    queryFn: () => api.listAvailablePlaybooks(conversationId),
    staleTime: 30 * 1000,
  });
  return { data: q.data ?? [], isLoading: q.isLoading, isError: q.isError };
}

export interface UseForceActions {
  /**
   * Force-takeover H_c→H_a. reason ≥20 backend (≥30 piso de UI). Guard + desfecho da resposta.
   * Devolve o `ConversationTakeover` criado (ou null em guard/erro) — a Tela 2 usa o `id`
   * devolvido para navegar à Tela 3 (Intervenção) carregando o `takeoverId` (D1/D9).
   */
  takeover: (reason: string, mode?: string) => Promise<ConversationTakeoverResult | null>;
  isTakeoverPending: boolean;
  takeoverError: unknown;
  /** Resposta REAL do servidor (ConversationTakeover) — origem do desfecho honesto. */
  takeoverResult: ConversationTakeoverResult | null;

  /** Force-playbook-switch. newPlaybookId real (available-playbooks) + reason ≥20. */
  playbookSwitch: (newPlaybookId: string, reason: string) => Promise<void>;
  isPlaybookSwitchPending: boolean;
  playbookSwitchError: unknown;
  /** Resposta REAL do servidor (PlaybookChangeHistory). */
  playbookSwitchResult: PlaybookChangeResult | null;
}

/**
 * D8 — ações de força em conversa H_c (módulo supervisor-actions, REAIS). Cada
 * ação tem guard anti duplo-submit (`isPending`), expõe `error` (async não engole
 * falha) e o resultado vem da RESPOSTA do servidor (nunca string hardcoded).
 */
export function useForceActions(conversationId: string): UseForceActions {
  const [isTakeoverPending, setIsTakeoverPending] = useState(false);
  const [takeoverError, setTakeoverError] = useState<unknown>(null);
  const [takeoverResult, setTakeoverResult] = useState<ConversationTakeoverResult | null>(null);

  const [isPlaybookSwitchPending, setIsPlaybookSwitchPending] = useState(false);
  const [playbookSwitchError, setPlaybookSwitchError] = useState<unknown>(null);
  const [playbookSwitchResult, setPlaybookSwitchResult] = useState<PlaybookChangeResult | null>(
    null,
  );

  const takeover = useCallback(
    async (reason: string, mode?: string): Promise<ConversationTakeoverResult | null> => {
      const trimmed = reason.trim();
      // Guard anti duplo-submit + texto vazio.
      if (trimmed.length === 0 || isTakeoverPending) return null;
      setIsTakeoverPending(true);
      setTakeoverError(null);
      try {
        const result = await api.forceTakeover(conversationId, {
          reason: trimmed,
          ...(mode !== undefined && mode.length > 0 ? { mode } : {}),
        });
        setTakeoverResult(result);
        return result;
      } catch (err) {
        setTakeoverError(err);
        return null;
      } finally {
        setIsTakeoverPending(false);
      }
    },
    [conversationId, isTakeoverPending],
  );

  const playbookSwitch = useCallback(
    async (newPlaybookId: string, reason: string): Promise<void> => {
      const trimmedReason = reason.trim();
      // Guard anti duplo-submit + campos vazios.
      if (newPlaybookId.length === 0 || trimmedReason.length === 0 || isPlaybookSwitchPending) {
        return;
      }
      setIsPlaybookSwitchPending(true);
      setPlaybookSwitchError(null);
      try {
        const result = await api.forcePlaybookSwitch(conversationId, {
          newPlaybookId,
          reason: trimmedReason,
        });
        setPlaybookSwitchResult(result);
      } catch (err) {
        setPlaybookSwitchError(err);
      } finally {
        setIsPlaybookSwitchPending(false);
      }
    },
    [conversationId, isPlaybookSwitchPending],
  );

  return {
    takeover,
    isTakeoverPending,
    takeoverError,
    takeoverResult,
    playbookSwitch,
    isPlaybookSwitchPending,
    playbookSwitchError,
    playbookSwitchResult,
  };
}
